/**
 * IPv4 / IPv6 address validation.
 *
 * Ported to TypeScript from the `validator` package (MIT licensed). IPv6
 * matching accepts an optional zone index (e.g. `fe80::1%eth0`) per RFC 4007.
 *
 * @module isIP
 */

/** A single IPv4 octet: `0`–`255` with no leading zeros. */
const IPv4SegmentFormat =
  "(?:[0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])";
/** Four dot-separated octets. */
const IPv4AddressFormat = `(${IPv4SegmentFormat}[.]){3}${IPv4SegmentFormat}`;
const IPv4AddressRegExp = new RegExp(`^${IPv4AddressFormat}$`);

/** A single IPv6 hextet: one to four hex digits. */
const IPv6SegmentFormat = "(?:[0-9a-fA-F]{1,4})";
/**
 * Full IPv6 matcher covering every legal `::` elision position and the
 * IPv4-mapped tail, with an optional trailing `%zone` index.
 */
const IPv6AddressRegExp = new RegExp(
  "^(" +
    `(?:${IPv6SegmentFormat}:){7}(?:${IPv6SegmentFormat}|:)|` +
    `(?:${IPv6SegmentFormat}:){6}(?:${IPv4AddressFormat}|:${IPv6SegmentFormat}|:)|` +
    `(?:${IPv6SegmentFormat}:){5}(?::${IPv4AddressFormat}|(:${IPv6SegmentFormat}){1,2}|:)|` +
    `(?:${IPv6SegmentFormat}:){4}(?:(:${IPv6SegmentFormat}){0,1}:${IPv4AddressFormat}|(:${IPv6SegmentFormat}){1,3}|:)|` +
    `(?:${IPv6SegmentFormat}:){3}(?:(:${IPv6SegmentFormat}){0,2}:${IPv4AddressFormat}|(:${IPv6SegmentFormat}){1,4}|:)|` +
    `(?:${IPv6SegmentFormat}:){2}(?:(:${IPv6SegmentFormat}){0,3}:${IPv4AddressFormat}|(:${IPv6SegmentFormat}){1,5}|:)|` +
    `(?:${IPv6SegmentFormat}:){1}(?:(:${IPv6SegmentFormat}){0,4}:${IPv4AddressFormat}|(:${IPv6SegmentFormat}){1,6}|:)|` +
    `(?::((?::${IPv6SegmentFormat}){0,5}:${IPv4AddressFormat}|(?::${IPv6SegmentFormat}){1,7}|:))` +
    ")(%[0-9a-zA-Z.]{1,})?$",
);

/** Which IP version to validate against. */
export interface IsIPOptions {
  /** `4` or `6`. When omitted, either version is accepted. */
  version?: 4 | 6 | "4" | "6";
}

/**
 * Reports whether a string is a valid IP address.
 *
 * @param ipAddress - The candidate address.
 * @param options - Restrict to a specific `version`; omit to accept v4 or v6.
 * @returns `true` if the string is a valid IP of the requested version.
 *
 * @example
 * ```ts
 * isIP("192.168.0.1"); // true
 * isIP("::1"); // true
 * isIP("192.168.0.1", { version: 6 }); // false
 * ```
 */
export default function isIP(
  ipAddress: string,
  { version }: IsIPOptions = {},
): boolean {
  if (version == null) {
    return (
      isIP(ipAddress, { version: "4" }) || isIP(ipAddress, { version: "6" })
    );
  }

  if (version.toString() === "4") {
    return IPv4AddressRegExp.test(ipAddress);
  }

  if (version.toString() === "6") {
    return IPv6AddressRegExp.test(ipAddress);
  }

  return false;
}
